import type {
  ActionKitResult,
  ActionKitBuilderErrorMapper,
  ActionKitErrorBase,
  ActionKitErrorUnknown,
  ActionKitReplaceError,
  Awaited,
} from './types';

export class ActionKitBuilder<
  TArgs extends any[],
  TResult,
  TError extends ActionKitErrorBase = ActionKitErrorUnknown
> {
  private fn: (...args: TArgs) => Promise<TResult> | TResult;
  private errorMappers: ActionKitBuilderErrorMapper<any>[] = [];

  constructor(fn: (...args: TArgs) => Promise<TResult> | TResult) {
    this.fn = fn;
  }

  onError<NextError extends ActionKitErrorBase>(
    mapper: (error: ActionKitErrorUnknown) => NextError,
  ): ActionKitBuilder<TArgs, TResult, NextError>;
  onError<NextError extends ActionKitErrorBase>(
    mapper: ActionKitBuilderErrorMapper<NextError>,
  ): ActionKitBuilder<TArgs, TResult, ActionKitReplaceError<TError, NextError>>;
  onError<NextError extends ActionKitErrorBase>(
    mapper: ActionKitBuilderErrorMapper<NextError>,
  ): ActionKitBuilder<
    TArgs,
    TResult,
    undefined extends ActionKitBuilderErrorMapper<NextError>
      ? ActionKitReplaceError<TError, NextError>
      : NextError
  > {
    this.errorMappers.push(mapper);
    return this as any;
  }

  async run(...args: TArgs): Promise<ActionKitResult<TResult, TError>> {
    try {
      const data = await this.fn(...args);
      return { success: true, data };
    } catch (error) {
      let errorResult = {
        code: 'unknown',
        data: error,
      } as ActionKitErrorUnknown;
      for (const mapper of this.errorMappers) {
        const mapped = mapper(errorResult);
        if (mapped) {
          errorResult = mapped;
        }
      }
      return {
        success: false,
        data: errorResult,
      } as ActionKitResult<TResult, TError>;
    }
  }
  /**
   * Devuelve una función que ejecuta la acción y aplica los mapeadores de error,
   * útil para usar directamente como handler o exportar como función estándar.
   */
  toFunction() {
    return this.run.bind(this as any);
  }
}

export function actionKitBuilder<
  T extends (...args: any[]) => any,
  TError extends ActionKitErrorBase = ActionKitErrorUnknown
>(fn: T) {
  type Infer = T extends (...args: infer A) => infer R
    ? { args: A; result: Awaited<R> }
    : never;
  return new ActionKitBuilder<Infer['args'], Infer['result'], TError>(fn);
}

export function actionKitFunction<
  T extends (...args: any[]) => any,
  TError extends ActionKitErrorBase = ActionKitErrorUnknown
>(fn: T) {
  return actionKitBuilder<T, TError>(fn).toFunction();
}
